import { RECORDED_LANDMARKS, readerFromRecorded } from "../core/landmarks.ts";
import type { SignalFrame } from "../core/types.ts";

const RIGHT_RING_END = 16;
const LEFT_RING_END = 32;

/**
 * Draws the eye region the detector is actually reading.
 *
 * Only the landmarks that end up in a recording are drawn, deliberately: if the
 * overlay showed all 478 points it would look reassuring even when the eye
 * indices were wrong. What is on screen is exactly what feeds the EAR.
 */
export class Overlay {
  readonly #canvas: HTMLCanvasElement;
  readonly #ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.#canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.#ctx = ctx;
  }

  resize(width: number, height: number): void {
    if (this.#canvas.width === width && this.#canvas.height === height) return;
    this.#canvas.width = width;
    this.#canvas.height = height;
  }

  clear(): void {
    this.#ctx.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
  }

  draw(frame: SignalFrame, closure: number, lowConfidence: boolean): void {
    const ctx = this.#ctx;
    this.clear();

    if (!frame.faceFound || frame.eyeLandmarks.length < RECORDED_LANDMARKS.length * 2) {
      ctx.fillStyle = "rgba(248,113,113,0.9)";
      ctx.font = "16px ui-monospace, monospace";
      ctx.save();
      ctx.scale(-1, 1);
      ctx.fillText("no face", -this.#canvas.width + 16, 30);
      ctx.restore();
      return;
    }

    const read = readerFromRecorded(frame.eyeLandmarks);

    // Green at rest, shading to amber as the eyes close, red when the frame is
    // too untrustworthy to decide on — so a false positive during a head turn is
    // visible as it happens rather than only in the log afterwards.
    const hue = lowConfidence ? 0 : 140 - closure * 100;
    ctx.strokeStyle = `hsl(${hue} 70% 60%)`;
    ctx.fillStyle = `hsl(${hue} 70% 60% / 0.12)`;
    ctx.lineWidth = 1.5;

    this.#ring(read, 0, RIGHT_RING_END);
    this.#ring(read, RIGHT_RING_END, LEFT_RING_END);

    ctx.fillStyle = `hsl(${hue} 90% 72%)`;
    for (let i = LEFT_RING_END; i < RECORDED_LANDMARKS.length; i++) {
      const p = read(RECORDED_LANDMARKS[i] as number);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  #ring(read: (i: number) => { x: number; y: number }, from: number, to: number): void {
    const ctx = this.#ctx;
    ctx.beginPath();
    for (let i = from; i < to; i++) {
      const p = read(RECORDED_LANDMARKS[i] as number);
      if (i === from) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}
